// Loading and sampling the 3D viewer's data: NCEI lakebed grids and NOAA
// temperature/current volumes (see greatlakes/three_d.py for the binary layout).
import { LAKES, LAKE_IDS } from "./geo.js";

// Land is stored as a small negative depth so linear interpolation crosses
// zero exactly at the shoreline; shaders cut everything above zero.
export const LAND_DEPTH = -4;
const FIELD_PADDING_DEGREES = 0.35;
const FIELD_CELL_LONGITUDE = 0.0125;
const FIELD_CELL_LATITUDE = 0.009;

export async function fetchJson(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) throw new Error(`Could not load ${url}.`);
  return response.json();
}

// Static .gz assets are normally served with Content-Encoding: gzip, but a
// proxy may strip it, so gzip bodies are also decompressed here.
export async function fetchBinary(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) {
    let message = "";
    try { message = (await response.json()).error || ""; } catch { /* not JSON */ }
    const error = new Error(message || `Could not load ${url}.`);
    error.status = response.status;
    throw error;
  }
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer, 0, Math.min(2, buffer.byteLength));
  if (bytes[0] === 0x1f && bytes[1] === 0x8b && typeof DecompressionStream === "function") {
    return new Response(new Blob([buffer]).stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer();
  }
  return buffer;
}

export async function loadTerrain(manifest, signal) {
  return Promise.all(LAKES.map(async (slug) => {
    const metadata = manifest.lakes?.[slug];
    const terrain = metadata?.terrain;
    if (!terrain?.url) throw new Error(`Lake ${slug} terrain is unavailable.`);
    const packed = new Uint16Array(await fetchBinary(`${terrain.url}?v=${encodeURIComponent(manifest.terrainVersion)}`, { signal }));
    if (packed.length !== terrain.rows * terrain.columns) throw new Error(`Lake ${slug} terrain grid is incomplete.`);
    const depths = new Float32Array(packed.length);
    for (let index = 0; index < packed.length; index += 1) {
      depths[index] = packed[index] === terrain.nodata ? LAND_DEPTH : packed[index] / 10;
    }
    return {
      slug,
      id: LAKE_IDS[slug],
      bounds: metadata.bounds,
      maxDepth: metadata.maxDepthMeters,
      rows: terrain.rows,
      columns: terrain.columns,
      north: terrain.northCenter,
      west: terrain.westCenter,
      cellLatitude: terrain.stride * terrain.pixelHeight,
      cellLongitude: terrain.stride * terrain.pixelWidth,
      depths
    };
  }));
}

function bilinear(values, rows, columns, row, column) {
  const r0 = Math.max(0, Math.min(rows - 1, Math.floor(row)));
  const c0 = Math.max(0, Math.min(columns - 1, Math.floor(column)));
  const r1 = Math.min(rows - 1, r0 + 1);
  const c1 = Math.min(columns - 1, c0 + 1);
  const fr = Math.max(0, Math.min(1, row - r0));
  const fc = Math.max(0, Math.min(1, column - c0));
  const top = values[r0 * columns + c0] * (1 - fc) + values[r0 * columns + c1] * fc;
  const bottom = values[r1 * columns + c0] * (1 - fc) + values[r1 * columns + c1] * fc;
  return top * (1 - fr) + bottom * fr;
}

// One lakebed field for all five lakes: depth (metres, land negative) and
// lake id per cell, with row 0 in the south. It drives shoreline drawing and
// clips every water layer exactly at the lakebed.
export function buildBedField(lakes) {
  const south = Math.min(...lakes.map((lake) => lake.bounds[0][0])) - FIELD_PADDING_DEGREES;
  const north = Math.max(...lakes.map((lake) => lake.bounds[1][0])) + FIELD_PADDING_DEGREES;
  const west = Math.min(...lakes.map((lake) => lake.bounds[0][1])) - FIELD_PADDING_DEGREES;
  const east = Math.max(...lakes.map((lake) => lake.bounds[1][1])) + FIELD_PADDING_DEGREES;
  const columns = Math.ceil((east - west) / FIELD_CELL_LONGITUDE) + 1;
  const rows = Math.ceil((north - south) / FIELD_CELL_LATITUDE) + 1;
  const depth = new Float32Array(rows * columns).fill(LAND_DEPTH);
  const id = new Uint8Array(rows * columns);
  for (const lake of lakes) {
    const [[lakeSouth, lakeWest], [lakeNorth, lakeEast]] = lake.bounds;
    const rowStart = Math.max(0, Math.floor((lakeSouth - south) / FIELD_CELL_LATITUDE));
    const rowEnd = Math.min(rows - 1, Math.ceil((lakeNorth - south) / FIELD_CELL_LATITUDE));
    const columnStart = Math.max(0, Math.floor((lakeWest - west) / FIELD_CELL_LONGITUDE));
    const columnEnd = Math.min(columns - 1, Math.ceil((lakeEast - west) / FIELD_CELL_LONGITUDE));
    for (let row = rowStart; row <= rowEnd; row += 1) {
      const latitude = south + row * FIELD_CELL_LATITUDE;
      const gridRow = (lake.north - latitude) / lake.cellLatitude;
      if (gridRow < 0 || gridRow > lake.rows - 1) continue;
      for (let column = columnStart; column <= columnEnd; column += 1) {
        const longitude = west + column * FIELD_CELL_LONGITUDE;
        const gridColumn = (longitude - lake.west) / lake.cellLongitude;
        if (gridColumn < 0 || gridColumn > lake.columns - 1) continue;
        const value = bilinear(lake.depths, lake.rows, lake.columns, gridRow, gridColumn);
        const index = row * columns + column;
        if (value > depth[index]) {
          depth[index] = value;
          if (value > 0) id[index] = lake.id;
        }
      }
    }
  }
  return { south, north, west, east, rows, columns, cellLatitude: FIELD_CELL_LATITUDE, cellLongitude: FIELD_CELL_LONGITUDE, depth, id };
}

export function bedDepthAt(field, latitude, longitude) {
  const row = (latitude - field.south) / field.cellLatitude;
  const column = (longitude - field.west) / field.cellLongitude;
  if (row < 0 || column < 0 || row > field.rows - 1 || column > field.columns - 1) return LAND_DEPTH;
  return bilinear(field.depth, field.rows, field.columns, row, column);
}

export function lakeIdAt(field, latitude, longitude) {
  const row = Math.round((latitude - field.south) / field.cellLatitude);
  const column = Math.round((longitude - field.west) / field.cellLongitude);
  if (row < 0 || column < 0 || row >= field.rows || column >= field.columns) return 0;
  return field.id[row * field.columns + column];
}

// The part of a straight line from where it first reaches one lake's water to
// where it last leaves it, with a sliver of shore; null if it never crosses.
export function lakeCrossing(field, lakeId, start, end, samples = 400) {
  const at = (index) => {
    const t = Math.max(0, Math.min(1, index / samples));
    return [start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t];
  };
  let first = -1;
  let last = -1;
  for (let index = 0; index <= samples; index += 1) {
    const [latitude, longitude] = at(index);
    if (lakeIdAt(field, latitude, longitude) === lakeId && bedDepthAt(field, latitude, longitude) > 0) {
      if (first < 0) first = index;
      last = index;
    }
  }
  if (first < 0 || last <= first) return null;
  return [at(first - 2), at(last + 2)];
}

function gridFrom(header) {
  return {
    rows: header.rows,
    columns: header.columns,
    south: header.south,
    north: header.north,
    west: header.west,
    east: header.east,
    cellLatitude: (header.north - header.south) / Math.max(1, header.rows - 1),
    cellLongitude: (header.east - header.west) / Math.max(1, header.columns - 1)
  };
}

export function parseVolume(buffer) {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(...new Uint8Array(buffer, 0, 4));
  if (magic !== "GL3D") throw new Error("The 3D water volume is not in the expected format.");
  const headerLength = view.getUint32(4, true);
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 8, headerLength)));
  const base = 8 + headerLength;
  const array = (name) => {
    const entry = header.arrays?.[name];
    return entry ? new Int16Array(buffer, base + entry.offset, entry.length) : null;
  };
  const depths = header.depths.map(Number);
  const nodata = header.nodata;
  const temperatureGrid = gridFrom(header.temperature);
  const temperatureRaw = array("temperature");
  const cells = temperatureGrid.rows * temperatureGrid.columns;
  if (!temperatureRaw || temperatureRaw.length !== cells * depths.length) throw new Error("The 3D temperature volume is incomplete.");
  const temperature = {
    ...temperatureGrid,
    range: header.temperature.range,
    bounds: null,
    raw: new Float32Array(temperatureRaw.length),
    filled: null
  };
  let minimumTemperature = Infinity;
  let maximumTemperature = -Infinity;
  for (let index = 0; index < temperatureRaw.length; index += 1) {
    const value = temperatureRaw[index] === nodata ? NaN : temperatureRaw[index] / header.temperature.scale;
    temperature.raw[index] = value;
    if (Number.isFinite(value)) {
      minimumTemperature = Math.min(minimumTemperature, value);
      maximumTemperature = Math.max(maximumTemperature, value);
    }
  }
  if (Number.isFinite(minimumTemperature) && Number.isFinite(maximumTemperature)) {
    temperature.bounds = [minimumTemperature, maximumTemperature];
  }
  temperature.filled = fillVolume(temperature.raw, depths.length, temperature.rows, temperature.columns);
  let currents = null;
  if (header.currents) {
    const east = array("east");
    const north = array("north");
    const grid = gridFrom(header.currents);
    if (east && north && east.length === grid.rows * grid.columns * depths.length) {
      currents = { ...grid, u: toFloat(east, nodata, header.currents.scale), v: toFloat(north, nodata, header.currents.scale) };
    }
  }
  return {
    lake: header.lake,
    model: header.model,
    validTime: header.validTime,
    forecastHour: header.forecastHour,
    depths,
    maxModelDepth: depths[depths.length - 1],
    temperature,
    currents
  };
}

function toFloat(values, nodata, scale) {
  const result = new Float32Array(values.length);
  for (let index = 0; index < values.length; index += 1) result[index] = values[index] === nodata ? NaN : values[index] / scale;
  return result;
}

// Fill gaps so GPU interpolation never blends with "no data": a few rounds of
// neighbour averaging per level, then the level above. Everything outside
// the water is clipped by the lakebed field, so filled values are never shown.
export function fillVolume(raw, levels, rows, columns) {
  const cells = rows * columns;
  const filled = new Float32Array(raw.length);
  let current = new Float32Array(cells);
  let next = new Float32Array(cells);
  for (let level = 0; level < levels; level += 1) {
    const offset = level * cells;
    let sum = 0;
    let count = 0;
    for (let cell = 0; cell < cells; cell += 1) {
      const value = raw[offset + cell];
      current[cell] = value;
      if (Number.isFinite(value)) { sum += value; count += 1; }
    }
    for (let pass = 0; pass < 6; pass += 1) {
      let changed = false;
      next.set(current);
      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const cell = row * columns + column;
          if (Number.isFinite(current[cell])) continue;
          let total = 0;
          let neighbours = 0;
          for (let dr = -1; dr <= 1; dr += 1) {
            const r = row + dr;
            if (r < 0 || r >= rows) continue;
            for (let dc = -1; dc <= 1; dc += 1) {
              const c = column + dc;
              if (c < 0 || c >= columns) continue;
              const value = current[r * columns + c];
              if (Number.isFinite(value)) { total += value; neighbours += 1; }
            }
          }
          if (neighbours) { next[cell] = total / neighbours; changed = true; }
        }
      }
      [current, next] = [next, current];
      if (!changed) break;
    }
    const fallback = count ? sum / count : (level ? NaN : 4);
    for (let cell = 0; cell < cells; cell += 1) {
      let value = current[cell];
      if (!Number.isFinite(value)) value = level ? filled[offset - cells + cell] : fallback;
      filled[offset + cell] = Number.isFinite(value) ? value : fallback;
    }
  }
  return filled;
}

// Fractional level index for a depth (levels are not evenly spaced).
export function levelPosition(depths, depth) {
  if (depth <= depths[0]) return 0;
  for (let index = 1; index < depths.length; index += 1) {
    if (depth <= depths[index]) return index - 1 + (depth - depths[index - 1]) / (depths[index] - depths[index - 1]);
  }
  return depths.length - 1;
}

function gridPosition(grid, latitude, longitude) {
  return [(latitude - grid.south) / grid.cellLatitude, (longitude - grid.west) / grid.cellLongitude];
}

export function insideGrid(grid, latitude, longitude) {
  const [row, column] = gridPosition(grid, latitude, longitude);
  return row >= -0.5 && column >= -0.5 && row <= grid.rows - 0.5 && column <= grid.columns - 0.5;
}

// Smoothly interpolated temperature (°C) at a point and depth, from the filled volume.
export function temperatureAt(volume, latitude, longitude, depth) {
  const grid = volume.temperature;
  if (!insideGrid(grid, latitude, longitude)) return NaN;
  const [row, column] = gridPosition(grid, latitude, longitude);
  const position = levelPosition(volume.depths, depth);
  const level = Math.floor(position);
  const fraction = position - level;
  const cells = grid.rows * grid.columns;
  const upper = bilinearLevel(grid.filled, grid.rows, grid.columns, level * cells, row, column);
  if (fraction <= 0 || level + 1 >= volume.depths.length) return upper;
  const lower = bilinearLevel(grid.filled, grid.rows, grid.columns, (level + 1) * cells, row, column);
  return upper + (lower - upper) * fraction;
}

function bilinearLevel(values, rows, columns, offset, row, column) {
  const r0 = Math.max(0, Math.min(rows - 1, Math.floor(row)));
  const c0 = Math.max(0, Math.min(columns - 1, Math.floor(column)));
  const r1 = Math.min(rows - 1, r0 + 1);
  const c1 = Math.min(columns - 1, c0 + 1);
  const fr = Math.max(0, Math.min(1, row - r0));
  const fc = Math.max(0, Math.min(1, column - c0));
  const a = values[offset + r0 * columns + c0];
  const b = values[offset + r0 * columns + c1];
  const c = values[offset + r1 * columns + c0];
  const d = values[offset + r1 * columns + c1];
  return (a * (1 - fc) + b * fc) * (1 - fr) + (c * (1 - fc) + d * fc) * fr;
}

// Temperature profile from the surface to the lakebed at one point.
export function temperatureProfile(volume, latitude, longitude, bedDepth) {
  const bottom = Math.max(0, bedDepth);
  const points = [];
  for (const depth of volume.depths) {
    if (depth > bottom) break;
    points.push([depth, temperatureAt(volume, latitude, longitude, depth)]);
  }
  if (bottom > 0 && (!points.length || points[points.length - 1][0] < bottom)) {
    points.push([bottom, temperatureAt(volume, latitude, longitude, Math.min(bottom, volume.maxModelDepth))]);
  }
  return points.filter(([, value]) => Number.isFinite(value));
}

// East and north current (m/s) on the current grid at one depth; NaN where there is no water.
export function currentsAtDepth(volume, depth) {
  const grid = volume.currents;
  if (!grid) return null;
  const cells = grid.rows * grid.columns;
  const position = levelPosition(volume.depths, depth);
  const level = Math.floor(position);
  const fraction = position - level;
  const nextLevel = Math.min(volume.depths.length - 1, level + 1);
  const uLevel = new Float32Array(cells);
  const vLevel = new Float32Array(cells);
  let maximum = 0;
  const speeds = [];
  for (let cell = 0; cell < cells; cell += 1) {
    const u0 = grid.u[level * cells + cell];
    const v0 = grid.v[level * cells + cell];
    const u1 = grid.u[nextLevel * cells + cell];
    const v1 = grid.v[nextLevel * cells + cell];
    let u;
    let v;
    if (Number.isFinite(u0) && Number.isFinite(u1)) { u = u0 + (u1 - u0) * fraction; v = v0 + (v1 - v0) * fraction; }
    else if (Number.isFinite(u0) && fraction < 0.5) { u = u0; v = v0; }
    else if (Number.isFinite(u1) && fraction >= 0.5) { u = u1; v = v1; }
    else { u = NaN; v = NaN; }
    uLevel[cell] = u;
    vLevel[cell] = v;
    if (Number.isFinite(u)) {
      const speed = Math.hypot(u, v);
      speeds.push(speed);
      maximum = Math.max(maximum, speed);
    }
  }
  speeds.sort((a, b) => a - b);
  const p98 = speeds.length ? speeds[Math.floor(speeds.length * 0.98)] : 0;
  return { ...grid, u: uLevel, v: vLevel, maximum, scaleMaximum: Math.max(0.08, p98), depth };
}

export function currentAt(field, latitude, longitude) {
  const row = (latitude - field.south) / field.cellLatitude;
  const column = (longitude - field.west) / field.cellLongitude;
  if (row < 0 || column < 0 || row > field.rows - 1 || column > field.columns - 1) return null;
  const r0 = Math.floor(row);
  const c0 = Math.floor(column);
  const r1 = Math.min(field.rows - 1, r0 + 1);
  const c1 = Math.min(field.columns - 1, c0 + 1);
  const fr = row - r0;
  const fc = column - c0;
  let u = 0;
  let v = 0;
  let weight = 0;
  for (const [r, c, w] of [[r0, c0, (1 - fr) * (1 - fc)], [r0, c1, (1 - fr) * fc], [r1, c0, fr * (1 - fc)], [r1, c1, fr * fc]]) {
    const index = r * field.columns + c;
    if (!Number.isFinite(field.u[index])) continue;
    u += field.u[index] * w;
    v += field.v[index] * w;
    weight += w;
  }
  if (weight < 0.5) return null;
  return [u / weight, v / weight];
}

// Depth (m) where each water column first crosses the target temperature,
// interpolated between model levels; NaN where it never does.
export function isothermDepths(volume, targetC) {
  const grid = volume.temperature;
  const cells = grid.rows * grid.columns;
  const result = new Float32Array(cells).fill(NaN);
  let count = 0;
  for (let cell = 0; cell < cells; cell += 1) {
    let previous = grid.raw[cell];
    if (!Number.isFinite(previous)) continue;
    if (previous === targetC) { result[cell] = 0; count += 1; continue; }
    for (let level = 1; level < volume.depths.length; level += 1) {
      const value = grid.raw[level * cells + cell];
      if (!Number.isFinite(value)) break;
      if ((previous - targetC) * (value - targetC) <= 0 && value !== previous) {
        const fraction = (targetC - previous) / (value - previous);
        result[cell] = volume.depths[level - 1] + fraction * (volume.depths[level] - volume.depths[level - 1]);
        count += 1;
        break;
      }
      previous = value;
    }
  }
  return { depths: result, count };
}
